import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge, Card, CardBody } from '@repo/ui';

interface DataReplicationCardProps {
  title: string;
  description: string;
  iconColor: 'blue' | 'green' | 'purple' | 'indigo' | 'amber' | 'pink' | 'emerald' | 'orange' | 'red' | 'teal' | 'cyan' | 'violet' | 'gray';
  isActive: boolean;
  manageRoute: string;
  features?: Array<{ icon: string; label: string }>;
}

export const DataReplicationCard: React.FC<DataReplicationCardProps> = ({
  title,
  description,
  iconColor,
  isActive,
  manageRoute,
  features = [],
}) => {
  const navigate = useNavigate();

  const colorClasses = {
    blue: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      icon: 'text-blue-600 dark:text-blue-400',
    },
    green: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      icon: 'text-green-600 dark:text-green-400',
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      icon: 'text-purple-600 dark:text-purple-400',
    },
    indigo: {
      bg: 'bg-indigo-100 dark:bg-indigo-900/30',
      icon: 'text-indigo-600 dark:text-indigo-400',
    },
    amber: {
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      icon: 'text-amber-600 dark:text-amber-400',
    },
    pink: {
      bg: 'bg-pink-100 dark:bg-pink-900/30',
      icon: 'text-pink-600 dark:text-pink-400',
    },
    emerald: {
      bg: 'bg-emerald-100 dark:bg-emerald-900/30',
      icon: 'text-emerald-600 dark:text-emerald-400',
    },
    orange: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      icon: 'text-orange-600 dark:text-orange-400',
    },
    red: {
      bg: 'bg-red-100 dark:bg-red-900/30',
      icon: 'text-red-600 dark:text-red-400',
    },
    teal: {
      bg: 'bg-teal-100 dark:bg-teal-900/30',
      icon: 'text-teal-600 dark:text-teal-400',
    },
    cyan: {
      bg: 'bg-cyan-100 dark:bg-cyan-900/30',
      icon: 'text-cyan-600 dark:text-cyan-400',
    },
    violet: {
      bg: 'bg-violet-100 dark:bg-violet-900/30',
      icon: 'text-violet-600 dark:text-violet-400',
    },
    gray: {
      bg: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-800/30',
      icon: 'text-xtnd-dark-500 dark:text-xtnd-dark-300',
    },
  };

  const colors = colorClasses[iconColor];

  return (
    <Card padding="lg" variant="outlined">
      <CardBody>
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 p-3 ${colors.bg} rounded-xl`}>
            <Icon name="HiDatabase" className={`h-8 w-8 ${colors.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-semibold text-xtnd-dark-800 dark:text-white">{title}</h3>
              {isActive && (
                <Badge variant="success" className="text-xs ml-2">
                  Active
                </Badge>
              )}
            </div>
            <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mb-4">{description}</p>

            {features.length > 0 && (
              <div className="flex items-center space-x-4 text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mb-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-1">
                    <Icon name={feature.icon as any} className="h-4 w-4" />
                    <span>{feature.label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Button
                onClick={() => navigate(manageRoute)}
                variant="primary"
                size="sm"
                leftIcon={<Icon name="HiCog" className="h-4 w-4" />}
              >
                Manage
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

